import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {
    allDaughtersOfWasteTqk,
    allWastesForBundleTqk,
    DivideFromWasteRequest,
    divideFromWasteVerb,
    ensureBundleForWaste,
    lastValidWasteByIdTqk,
    NewWasteFood,
    Waste,
    wasteByIdTqk,
    WasteFlavorStub,
    WastePatch,
    wastesForBundleTqk,
    watchBundleForWasteTqo,
    watchWasteByIdTqo,
} from "../../rpc/webrpc-client.gen.ts";
import {useBirthableWasteFlavors, useBirthableWasteFlavorsByCode} from "../../wasteFlavor/wasteFlavor.tsx";
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Input as JoyInput,
    Link as JoyLink,
    Modal,
    ModalDialog,
    Stack
} from "@mui/joy";
import Grid from "@mui/material/Grid";
import {FormEvent, Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {
    allWasteRevisionStubsTqxk,
    useActiveDaughtersOfWaste,
    useMothership,
    usePatchWasteMutation,
    useRetireWasteMutation
} from "../../tq/tqx.tsx";
import {Controller, useController, useForm} from "react-hook-form";
import {NumericFormat} from "react-number-format";
import {useTranslation} from "react-i18next";
import {ActionButton} from "../../components/ActionButton.tsx";
import {makeWasteFormPatch, WasteForm, wasteFormToNewWasteFood, wasteToWasteForm} from "../../waste/editor.tsx";
import {WasteRouterLink} from "../../waste/routerLink.tsx";
import {SuspenseDialog} from "../../components/SuspenseDialog.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../../util/hookutil.tsx";
import {useInfallibleQuery} from "../../util/useInfallibleQuery.tsx";
import {useBomb} from "../../bomb/bomb.tsx";
import {PageBody, PageHeader, PageShell} from "../../routing/shell.tsx";
import {TopBar} from "../../components/TopBar.tsx";

type DaughterFormField = "invVolm3";
type DaughterForm = Record<DaughterFormField, string>;

export function useSplitWasteMutation() {
    const tqc = useQueryClient();
    const mama = useMothership();
    const onSuccess = useCallback((daughter: Waste, {oyaId}: DivideFromWasteRequest) => {
        tqc.setQueryData(
            wasteByIdTqk({wasteId: daughter.id}), {waste: daughter});
        return Promise.all([
            tqc.invalidateQueries({queryKey: wasteByIdTqk({wasteId: oyaId})}),
            tqc.invalidateQueries({queryKey: lastValidWasteByIdTqk({wasteId: oyaId})}),
            tqc.invalidateQueries({queryKey: allWasteRevisionStubsTqxk({wasteId: oyaId})}),
            tqc.invalidateQueries({queryKey: allDaughtersOfWasteTqk({wasteId: oyaId})}),
            ensureBundleForWaste({tqc, mama, args: {wasteId: oyaId}})
                .then((rpc) => {
                    if (rpc.bundle) {
                        const bundle = rpc.bundle;
                        return Promise.all([
                            tqc.invalidateQueries({queryKey: wastesForBundleTqk({bundleId: bundle.id})}),
                            tqc.invalidateQueries({queryKey: allWastesForBundleTqk({bundleId: bundle.id})}),
                        ]);
                    }
                }),
        ]);
    }, [mama, tqc]);
    return useMutation({
        mutationKey: [divideFromWasteVerb],
        mutationFn: (args: DivideFromWasteRequest) => mama.divideFromWaste(args)
            .then(tqrv => tqrv.daughter),
        onSuccess,
    });
}

type WasteSplitForm = Record<string, DaughterForm | undefined>;

const fillableDaughterFields: DaughterFormField[] = ["invVolm3"] as const;

function useExistingWasteSplitForm(xkidsByCode: Map<string, WasteForm> | undefined): InfallibleUseResult<WasteSplitForm> {
    const {data: flavorsByCode} = useBirthableWasteFlavorsByCode();
    const rv = useMemo(() => {
        if (!xkidsByCode || !flavorsByCode) {
            return pendingUseResult;
        } else {
            const rmap = new Map<Waste["flavor"]["code"], DaughterForm>();
            flavorsByCode.forEach((flavor) => {
                const xr = xkidsByCode.get(flavor.code);
                const ph
                    = Object.fromEntries(fillableDaughterFields.map(fn => {
                    return [fn, xr ? xr[fn] : ""];
                })) as DaughterForm;
                rmap.set(flavor.code, ph);
            });
            const rv = Object.fromEntries(rmap.entries());
            const subtotals = calcDaughterTotals(rv);
            rv.total = subtotals;
            return successUseResult(rv);
        }
    }, [flavorsByCode, xkidsByCode]);
    return rv;
}

function calcDaughterTotals(formValues: WasteSplitForm): Record<DaughterFormField, string> {
    const nrv
        = Object.fromEntries(
        fillableDaughterFields.map(fieldName => [fieldName, 0])) as
        Record<DaughterFormField, number>;
    for (const k in formValues) {
        if (k === "total") {
            continue;
        }
        const vo = formValues[k];
        if (vo) {
            fillableDaughterFields.forEach(fieldName => {
                const sv = vo[fieldName];
                if (!sv) {
                    return;
                }
                const v = Number.parseFloat(sv);
                const subtotal = nrv[fieldName] + v;
                nrv[fieldName] = subtotal;
            });
        }
    }

    const rv = Object.fromEntries(
        fillableDaughterFields.map(fieldName =>

            [fieldName, nrv[fieldName].toString()])) as Record<DaughterFormField, string>;
    return rv;
}

interface SpawnAction {
    cmd: "a";
    food: DivideFromWasteRequest;
}

interface PatchAction {
    cmd: "u";
    wasteId: string;
    patch: WastePatch;
}

interface RetireAction {
    cmd: "d";
    wasteId: string;
}

type Action = SpawnAction | PatchAction | RetireAction;

function isDaughterEqual(a: Record<DaughterFormField, string>, b: Record<DaughterFormField, string>) {
    for (const fn of fillableDaughterFields) {
        if (a[fn] !== b[fn]) {
            return false;
        }
    }
    return true;
}

function isDaughterFormFull(maybe: DaughterForm | undefined): maybe  is DaughterForm {
    if (!maybe) {
        return false;
    }
    const isEmpty = fillableDaughterFields.reduce((acc, fn) => {
        if (!acc) {
            return acc;
        }
        const fv = maybe[fn];
        const rv = fv === "" || fv === "0";
        return rv;
    }, true);
    return !isEmpty;
}

interface FigureSyncActionsArgs {
    oya: Waste;
    xkidsByCode: Map<string, WasteForm>;
    newForm: WasteSplitForm;
    flavorsByCode: Map<string, WasteFlavorStub>;
}

function figureSyncActions(
    {
        oya,
        xkidsByCode,
        newForm,
        flavorsByCode
    }: FigureSyncActionsArgs): Action[] {

    const newKidsByCode
        = new Map<string, Record<DaughterFormField, string>>();
    for (const code in newForm) {
        if (code === "total") {
            continue;
        }
        const dr = newForm[code];
        if (isDaughterFormFull(dr)) {
            newKidsByCode.set(code, dr);
        }
    }

    const spawns: SpawnAction[] = [];
    const patches: PatchAction[] = [];
    const retires: RetireAction[] = [];

    for (const [code, waste] of xkidsByCode.entries()) {
        if (!newKidsByCode.has(code)) {
            retires.push({cmd: "d", wasteId: waste.id!});
        } else {
            const newKid = newKidsByCode.get(code)!;
            if (!isDaughterEqual(waste, newKid)) {
                const flavor = flavorsByCode.get(code);
                if (!flavor) {
                    throw new Error(`can't find waste flavor code ${code}`);
                }
                const after: WasteForm = {
                    invManiLinked: true,
                    packingModeId: null,
                    manifestTypeId: null,
                    invQty: "",
                    maniQty: "",
                    invKg: "",
                    maniKg: "",
//                    invVolm3: "",
                    maniVolm3: "",
                    manifestNumber: "",
                    bara: "",
                    bags: "",

                    flavor,
                    destination: null,
                    ...newKid,
                };
                const patch = makeWasteFormPatch({prev: waste, next: after});
                patches.push({cmd: "u", wasteId: waste.id!, patch});
            }
            newKidsByCode.delete(code);
        }
    }

    for (const [code, daughter] of newKidsByCode.entries()) {
        const flavor = flavorsByCode.get(code);
        if (!flavor) {
            throw new Error(`can't find waste flavor code ${code}`);
        }
        const food: NewWasteFood = {
            ...wasteFormToNewWasteFood({
                bags: "",
                bara: "",
                invKg: "",
                invManiLinked: true,
                invQty: "",
                maniKg: "",
                maniQty: "",
                maniVolm3: "",
                manifestNumber: "",
                manifestTypeId: null,
                packingModeId: null,

                flavor,
                destination: null,
                ...daughter
            }),
            notational: true,
        };
        spawns.push({cmd: "a", food: {oyaId: oya.id, food}});
    }

    return [...spawns, ...patches, ...retires];
}

function WasteSplitPage() {
    const {id: oyaId} = Route.useParams();
    const mama = useMothership();
    const {data: bundle, isSuccess: isBundleSuccess}
        = useInfallibleQuery(watchBundleForWasteTqo(mama, {wasteId: oyaId}));
    const {data: oya, isSuccess: isOyaSuccess}
        = useQuery(watchWasteByIdTqo(mama, {wasteId: oyaId}));
    const oyaFormValues: WasteForm | undefined = oya
        ? wasteToWasteForm(oya)
        : undefined;
    const {data: xkids} = useActiveDaughtersOfWaste({wasteId: oyaId});
    const xkidsByCode = useMemo(() => {
        if (xkids) {
            return new Map<string, WasteForm>(xkids.map((kid: Waste) =>
                [kid.flavor.code, wasteToWasteForm(kid)]));
        } else {
            return undefined;
        }
    }, [xkids]);

    const {mutateAsync: performSplitWaste} = useSplitWasteMutation();
    const {mutateAsync: performPatchWaste} = usePatchWasteMutation();
    const {mutateAsync: performRetireWaste} = useRetireWasteMutation();
    const router = useRouter();
    const {data: birthableFlavorsData} = useBirthableWasteFlavors();
    const birthableFlavors = useMemo(() => birthableFlavorsData ?? [],
        [birthableFlavorsData]);
    const {data: flavorsByCode} = useBirthableWasteFlavorsByCode();
    const {data: defaultFormValues, isSuccess: isFormDefaultReady}
        = useExistingWasteSplitForm(xkidsByCode);
    const {t} = useTranslation();
    const {
        control,
        reset: resetForm,
        handleSubmit,
        getValues,
        formState: {
            isValid,
            isDirty
        },
    } = useForm<WasteSplitForm>();
    useEffect(() => {
        if (defaultFormValues) {
            resetForm(defaultFormValues);
        }
    }, [defaultFormValues, resetForm]);

    const bomb = useBomb();

    const onPerformSync = useCallback((newForm: WasteSplitForm) => {
        if (xkidsByCode && flavorsByCode && oya) {
            const syncActions = figureSyncActions({
                oya, xkidsByCode, newForm, flavorsByCode
            })
            // console.log(syncActions);
            const actionPerformers: Promise<unknown>[] = syncActions.map(a => {
                switch (a.cmd) {
                    case "a":
                        return performSplitWaste(a.food);
                    case "u":
                        return performPatchWaste({wasteId: a.wasteId, patch: a.patch});
                    case "d":
                        return performRetireWaste({wasteId: a.wasteId});
                }
            });
            return Promise.all(actionPerformers);
        } else {
            return Promise.resolve();
        }
    }, [xkidsByCode, flavorsByCode, oya,
        performSplitWaste, performPatchWaste, performRetireWaste]);

    const onSubmit = useCallback((evt: FormEvent<HTMLFormElement>) => {
        const handler = handleSubmit((formValues) => {
            // console.log(formValues);
            return onPerformSync(formValues).then(() => {
                router.history.back();
            });
        }, () => {
            setIsConfirmationRequested(true);
            return Promise.resolve();
        });
        handler(evt).catch(bomb);
    }, [router.history, bomb, handleSubmit, onPerformSync]);

    const {field: totalsField} = useController<WasteSplitForm>({
        control,
        name: "total",
        rules: {
            validate: (values, formValues) => {
                const daughterTotals = calcDaughterTotals(formValues);
                const isValid =
                    fillableDaughterFields.reduce((acc, fieldName) => {
                        if (!acc) {
                            return acc;
                        }

                        return !!values && !!oyaFormValues &&
                            values[fieldName] === daughterTotals[fieldName] &&
                            values[fieldName] === oyaFormValues[fieldName];
                    }, true);
                return isValid;
            }
        },
    });

    const updateTotals = useCallback(() => {
        const formValues = getValues();
        const totals = calcDaughterTotals(formValues);
        totalsField.onChange({
            target: {
                name: totalsField.name,
                value: totals,
            }
        });
    }, [getValues, totalsField]);

    const [isConfirmationRequested, setIsConfirmationRequested]
        = useState<boolean>(false);

    if (!isBundleSuccess) {
        return <SuspenseDialog message="fetching bundle"/>
    }

    if (!bundle) {
        throw new Error(`no bundle for waste ${oyaId}`);
    }

    if (!isOyaSuccess) {
        return <SuspenseDialog message="fetching waste"/>
    }

    if (!oya) {
        throw new Error(`no waste with id ${oyaId}`);
    }

    if (!isFormDefaultReady) {
        return <SuspenseDialog message="preparing delivery room"/>
    }

    const actionCosmetics = isValid
        ? {color: "primary", variant: "solid"} as const
        : {color: "danger", variant: "soft"} as const;

    // return <PageShell>
    //     <PageBody><Box>hello world</Box></PageBody>
    // </PageShell>


    return <PageShell>
        <PageHeader>
            <TopBar/>
        </PageHeader>
        <PageBody>

            <form id="uchiwake" onSubmit={(evt) => {
                onSubmit(evt);
            }}>
                <Stack direction="column">
                    <Stack direction="row"
                           sx={{flex: 1, alignItems: "center", justifyContent: "center", fontSize: "xl", pb: 1.8}}>
                        <JoyLink component={WasteRouterLink} wasteId={oyaId} sx={{pr: 2}}>
                            {oya.flavor.displayName}
                        </JoyLink>
                        <NumericFormat value={oyaFormValues?.invVolm3}
                                       readOnly={true}
                                       customInput={JoyInput}
                                       startDecorator={t("invoiceVolume")}
                                       endDecorator="m³"
                                       decimalScale={1}
                                       sx={{maxWidth: "11rem"}}
                                       slotProps={{input: {style: {"textAlign": "right"}}}}
                                       valueIsNumericString={true}
                        />
                    </Stack>

                    <Grid container sx={{flex: 1}}>
                        {birthableFlavors.map(flavor => {
                            return <Fragment key={flavor.displayName}>
                                <Grid size={{xs: 6, md: 3}} sx={{
                                    display: "flex",
                                    justifyContent: "left",
                                    fontSize: "sm",
                                    pl: 1.2,
                                }}>
                                    {flavor.displayName}
                                </Grid>
                                <Grid size={{xs: 6, md: 3}}>
                                    <Controller<WasteSplitForm>
                                        control={control}
                                        render={({field}) => {
                                            return <Stack direction="row">
                                                <NumericFormat value={field.value?.invVolm3 ?? ""}
                                                               customInput={JoyInput}
                                                               endDecorator="m³"
                                                               decimalScale={1}
                                                               sx={{maxWidth: "6rem"}}
                                                               slotProps={{
                                                                   input: {
                                                                       style: {"textAlign": "right"},
                                                                       type: "number",
                                                                   }
                                                               }}
                                                               valueIsNumericString={true}
                                                               onValueChange={(values) => {
                                                                   const evt = {
                                                                       target: {
                                                                           name: field.name,
                                                                           value: {
                                                                               invVolm3: values.value,
                                                                           },
                                                                       }
                                                                   } as const;
                                                                   field.onChange(evt);
                                                                   updateTotals();
                                                               }}
                                                />
                                            </Stack>
                                        }}
                                        name={flavor.code}/>
                                </Grid>
                            </Fragment>
                        })}
                    </Grid>

                    <Stack direction="row"
                           sx={{
                               flex: 1, alignItems: "center", justifyContent: "center",
                               fontSize: "xl", pb: 1.8, pt: 1.8
                           }}>
                        <Box sx={{pr: 2}}>{t("total")}</Box>
                        <NumericFormat value={getValues("total.invVolm3")}
                                       readOnly={true}
                                       customInput={JoyInput}
                                       endDecorator="m³"
                                       decimalScale={1}
                                       sx={{maxWidth: "8rem"}}
                                       slotProps={{input: {style: {"textAlign": "right"}}}}
                                       valueIsNumericString={true}
                        />
                    </Stack>

                    <Stack direction="row" sx={{justifyContent: "center", pt: 2}}>
                        <ActionButton type="submit" {...actionCosmetics}
                                      disabled={!xkids || !flavorsByCode || !isDirty}
                                      sx={{fontSize: "xl", px: 4, py: 1.6}}>
                            {t("inspection.startSplit")}
                        </ActionButton>
                    </Stack>
                </Stack>
            </form>
        </PageBody>
        <Modal open={isConfirmationRequested}
               onClose={() => {
                   setIsConfirmationRequested(false);
               }}
               sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
            <ModalDialog variant="outlined">
                <DialogTitle>
                    <Box sx={{fontSize: "lg"}}>{t("inspection.partialSplitConfirmTitle")}</Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{fontSize: "lg"}}>{t("inspection.partialSplitConfirmMessage")}</Box>
                </DialogContent>
                <DialogActions>
                    <ActionButton color="danger" variant="solid"
                                  disabled={!xkids || !flavorsByCode}
                                  perform={() => {
                                      setIsConfirmationRequested(false);
                                      const newForm = getValues();
                                      return onPerformSync(newForm).then(() => {
                                          router.history.back();
                                      });
                                  }}>
                        {t("inspection.performPartialSplit")}
                    </ActionButton>
                    <ActionButton color="neutral" variant="solid"
                                  perform={() => {
                                      setIsConfirmationRequested(false);
                                      return Promise.resolve();
                                  }}>
                        {t("cancel")}
                    </ActionButton>
                </DialogActions>
            </ModalDialog>
        </Modal>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/waste-split/$id")({
    component: WasteSplitPage,
});

